import { fromDay, startOfMonthDay, toDay, weekdayOfDay, type DayNumber, type ISODate } from "@/engine/date";
import { HOLIDAY_CATALOGS, holidayDisplayName } from "@/engine/holiday-catalogs";
import type { CalendarDefinition } from "@/engine/calendar";
import type { ExecutionTask, Project, Schedule } from "@/engine/types";
import { isOverdue } from "@/store/execution-task-ops";

/**
 * Pure layout for the project calendar view. Independent from the date-picker calendar and from the
 * Gantt time scale: everything here is day-granular and works on inclusive [start, end] ranges.
 */

export type CalendarLayer = "wbs" | "activity" | "execution" | "deadline";
export const ALL_LAYERS: CalendarLayer[] = ["wbs", "activity", "execution", "deadline"];
export const DEFAULT_LAYERS: CalendarLayer[] = ["activity", "execution"];
export const LAYER_LABEL: Record<CalendarLayer, string> = { wbs: "WBS", activity: "작업 · 마일스톤", execution: "Task", deadline: "마감" };

export type CalendarEventKind = "wbs" | "activity" | "milestone" | "execution" | "deadline";

export interface CalendarEvent {
  /** unique per kind+ref */
  id: string;
  kind: CalendarEventKind;
  refId: string;
  title: string;
  /** inclusive */
  start: ISODate;
  end: ISODate;
  /** schedule facts */
  wbs?: string;
  depth?: number;
  critical?: boolean;
  /** execution facts */
  done?: boolean;
  overdue?: boolean;
  /** true when the execution task has only one of start/due */
  partial?: boolean;
  assignees?: string[];
}

export const KR_HOLIDAYS_NAMES: Record<string, string> = Object.fromEntries(
  (HOLIDAY_CATALOGS.find((catalog) => catalog.id === "kr")?.days ?? []).map((day) => [day.date, day.name]),
);

export function getHolidayName(iso: ISODate, calendar?: CalendarDefinition): string | undefined {
  return holidayDisplayName(iso, calendar);
}

/** Day-cell floor. Below 7× this the month grid scrolls horizontally instead of crushing cells. */
export const CALENDAR_DAY_MIN_WIDTH = 120;
export const CALENDAR_GRID_MIN_WIDTH = CALENDAR_DAY_MIN_WIDTH * 7;

/** Sunday-first week containing `day`. */
export function startOfWeekDay(day: DayNumber): DayNumber {
  return day - weekdayOfDay(day);
}

/** 6 rows × 7 days covering the month of `anchor` (Sunday-first). */
export function monthGrid(anchor: ISODate): DayNumber[][] {
  const first = startOfMonthDay(toDay(anchor));
  const start = startOfWeekDay(first);
  const rows: DayNumber[][] = [];
  for (let r = 0; r < 6; r++) rows.push(Array.from({ length: 7 }, (_, c) => start + r * 7 + c));
  return rows;
}

export function weekRow(anchor: ISODate): DayNumber[] {
  const start = startOfWeekDay(toDay(anchor));
  return Array.from({ length: 7 }, (_, c) => start + c);
}

export function monthOf(iso: ISODate): string {
  return iso.slice(0, 7);
}

/* ---------------------------------------------------------------- events */

export interface BuildEventsInput {
  project: Pick<Project, "tasks" | "executionTasks">;
  schedule: Schedule | null;
  layers: CalendarLayer[];
  today: ISODate;
  /** limit execution tasks (assignee / bucket / status filters) */
  executionFilter?: (t: ExecutionTask) => boolean;
  /** WBS bands deeper than this are skipped (default 1 → phases and their children) */
  maxWbsDepth?: number;
}

export function buildEvents(input: BuildEventsInput): CalendarEvent[] {
  const { project, schedule, layers, today, executionFilter, maxWbsDepth = 1 } = input;
  const out: CalendarEvent[] = [];
  const on = new Set(layers);
  if (schedule) {
    for (const id of schedule.order) {
      const s = schedule.tasks.get(id)!;
      const t = project.tasks.find((x) => x.id === id);
      if (!t) continue;
      if (s.isSummary) {
        if (on.has("wbs") && s.depth <= maxWbsDepth) out.push({ id: `wbs:${id}`, kind: "wbs", refId: id, title: t.name || "이름 없음", start: s.start, end: s.finish, wbs: s.wbs, depth: s.depth, critical: s.critical });
      } else if (on.has("activity")) {
        out.push({
          id: `${s.isMilestone ? "ms" : "act"}:${id}`,
          kind: s.isMilestone ? "milestone" : "activity",
          refId: id,
          title: t.name || "이름 없음",
          start: s.start,
          end: s.isMilestone ? s.start : s.finish,
          wbs: s.wbs,
          depth: s.depth,
          critical: s.critical,
          done: s.percentComplete >= 100,
        });
      }
      if (on.has("deadline") && t.deadline) {
        out.push({ id: `dl:${id}`, kind: "deadline", refId: id, title: `${t.name || "이름 없음"} 마감`, start: t.deadline, end: t.deadline, wbs: s.wbs, critical: s.deadlineMissed });
      }
    }
  }
  if (on.has("execution")) {
    for (const x of project.executionTasks) {
      if (executionFilter && !executionFilter(x)) continue;
      const start = x.start ?? x.due;
      const end = x.due ?? x.start;
      if (!start || !end) continue;
      out.push({
        id: `x:${x.id}`,
        kind: "execution",
        refId: x.id,
        title: x.title || "제목 없음",
        start: start <= end ? start : end,
        end: start <= end ? end : start,
        done: x.status === "done",
        overdue: isOverdue(x, today),
        partial: !x.start || !x.due,
        assignees: x.assignees,
      });
    }
  }
  return out;
}

/** Execution tasks with no dates at all (the "일정 미정" tray). */
export function unscheduledTasks(tasks: ExecutionTask[], filter?: (t: ExecutionTask) => boolean): ExecutionTask[] {
  return tasks.filter((t) => !t.start && !t.due && (!filter || filter(t)));
}

/* ---------------------------------------------------------------- week packing */

export interface Segment {
  event: CalendarEvent;
  /** 0..6 column of the first visible day */
  col: number;
  /** number of visible days */
  span: number;
  /** stacking row inside the week */
  lane: number;
  continuesLeft: boolean;
  continuesRight: boolean;
}

const KIND_RANK: Record<CalendarEventKind, number> = { wbs: 0, activity: 1, milestone: 2, deadline: 3, execution: 4 };

/**
 * Cut events to a 7-day window and pack them into lanes: longer/earlier bars first, schedule layers
 * above execution tasks, so the same event keeps a stable lane from week to week where possible.
 */
export function layoutWeek(events: CalendarEvent[], weekStart: DayNumber): Segment[] {
  const weekEnd = weekStart + 6;
  const segs: Omit<Segment, "lane">[] = [];
  for (const e of events) {
    const s = toDay(e.start);
    const f = toDay(e.end);
    if (f < weekStart || s > weekEnd) continue;
    const cs = Math.max(s, weekStart);
    const cf = Math.min(f, weekEnd);
    segs.push({ event: e, col: cs - weekStart, span: cf - cs + 1, continuesLeft: s < weekStart, continuesRight: f > weekEnd });
  }
  segs.sort((a, b) => KIND_RANK[a.event.kind] - KIND_RANK[b.event.kind] || a.col - b.col || b.span - a.span || a.event.title.localeCompare(b.event.title, "ko"));
  const lanes: Array<Array<[number, number]>> = [];
  const out: Segment[] = [];
  for (const s of segs) {
    const a = s.col;
    const b = s.col + s.span - 1;
    let lane = 0;
    for (; lane < lanes.length; lane++) if (lanes[lane]!.every(([x, y]) => b < x || a > y)) break;
    if (lane === lanes.length) lanes.push([]);
    lanes[lane]!.push([a, b]);
    out.push({ ...s, lane });
  }
  return out;
}

/** Events touching a single day (used by the day cell tooltip / overflow popover). */
export function eventsOnDay(events: CalendarEvent[], day: DayNumber): CalendarEvent[] {
  const iso = fromDay(day);
  return events.filter((e) => e.start <= iso && e.end >= iso);
}

/** Move an execution task's date range so it starts (or ends when only `due` exists) on `target`. */
export function moveExecutionDates(task: Pick<ExecutionTask, "start" | "due">, target: ISODate): { start?: ISODate; due?: ISODate } {
  if (task.start && task.due) {
    const span = toDay(task.due) - toDay(task.start);
    return { start: target, due: fromDay(toDay(target) + span) };
  }
  if (task.start) return { start: target };
  return { due: target };
}
